import { Form, InputGroup } from '@valid-eval/shared-react-components';
import { Controller } from 'react-hook-form';
import AutosizeInput from 'react-input-autosize';
import TagsInput from 'react-tagsinput';
import cx from 'classnames';

import { required as requiredVal } from 'components/FormFields/validations';

import FieldError from './FieldError';
import HelpText from './HelpText';
import Label from './Label';
import useFormFieldState from './useFormFieldState';
import Styles from './TagsInput.module.scss';
import { useState } from 'react';

export const splitBySpecialChars = (values: string) => {
  const separators = [',', ';', '\n', '\r', '\r\n'];
  return values.split(new RegExp(separators.join('|'))).map((d) => d.trim());
};

export type InputProps = {
  after?: React.ReactNode;
  before?: React.ReactNode;
  id?: string;
  label?: string;
  name: string;
  placeholder?: string;
  rules?: { validate?: Record<string, any> };
  disabled?: boolean;
  required?: boolean;
  helpText?: string;
  autoComplete?: 'on' | 'off';
  autoFocus?: boolean;
  maxLength?: number;
  validateBeforeAdd?: (value: string) => boolean;
};

const Input = ({
  after,
  before,
  id,
  label,
  name,
  placeholder,
  rules,
  disabled,
  required,
  helpText,
  autoComplete,
  autoFocus,
  maxLength,
  validateBeforeAdd,
}: InputProps) => {
  const { control, isValid, isInvalid, error } = useFormFieldState(name);
  const [invalidTags, setInvalidTags] = useState<string[]>([]);

  rules = rules || {};
  rules = required ? { ...rules, validate: { required: requiredVal, ...rules.validate } } : rules;

  return (
    <>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value, ...fieldProps } }) => {
          return (
            <Form.Group className="mb-2">
              <Label htmlFor={id || name} label={label} />
              <HelpText text={helpText} />
              <InputGroup hasValidation>
                <> {before} </>
                <TagsInput
                  addOnPaste
                  onlyUnique
                  addOnBlur
                  addKeys={[13, 186, 188]} // 13 is enter, 186 is semicolon, 188 is comma
                  pasteSplit={splitBySpecialChars}
                  value={value ?? []}
                  onChange={(value: string[]) => {
                    const valid = value.filter((v) => !validateBeforeAdd || validateBeforeAdd?.(v));
                    const invalid = value.filter(
                      (v) => validateBeforeAdd && !validateBeforeAdd?.(v),
                    );
                    onChange(valid);
                    setInvalidTags(invalid);
                  }}
                  className={cx(Styles.TagsInput, {
                    'is-invalid': isInvalid,
                    'is-valid': isValid,
                  })}
                  inputProps={{
                    id: id || name,
                    placeholder: placeholder,
                    autoComplete: autoComplete,
                    maxLength: maxLength,
                    autoFocus: autoFocus,
                  }}
                  disabled={disabled}
                  renderInput={(props: any) => <AutosizeInput {...props} />}
                  {...fieldProps}
                />
                <> {after} </>
                <FieldError show={isInvalid} message={String(error?.message)} />
                {invalidTags?.length > 0 && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    The following values couldn't be added because they are not well-formed:
                    <ul className="text-danger">
                      {invalidTags.map((tag) => (
                        <li key={tag}>{tag}</li>
                      ))}
                    </ul>
                  </Form.Control.Feedback>
                )}
              </InputGroup>
            </Form.Group>
          );
        }}
      />
    </>
  );
};

export default Input;
